/**
 * GlassmorphismButton — Web-optimized glassmorphism buttons
 *
 * Variants:
 * - primary: Red gradient for main actions (Play, Watch)
 * - secondary: Gray gradient for secondary actions (Preview, Trailer)
 * - icon: Circular button for like/dislike/bookmark
 *
 * Non-blur glassmorphism design (gradient backgrounds, no backdrop-blur)
 * for broad browser compatibility.
 *
 * Ported from App-WebTV/src/components/common/GlassmorphismButton.jsx
 * Adapted: removed spatial navigation (useFocusable), uses standard hover/click.
 */
"use client";

import React from "react";

// ============================================================================
// STYLES
// ============================================================================

const baseButtonStyles = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 48,
    padding: "0 28px",
    fontFamily: "'AnjomanMax', sans-serif",
    fontSize: 18,
    fontWeight: 600,
    borderRadius: 10,
    border: "2px solid rgba(255, 255, 255, 0.25)",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.4)",
    cursor: "pointer",
    transition: "all 0.15s ease",
    textShadow: "0 1px 2px rgba(0,0,0,0.8)",
    gap: 8,
};

const variantStyles = {
    primary: {
        color: "#FFFFFF",
        background: "rgba(180, 30, 30, 0.75)",
    },
    secondary: {
        color: "#FFFFFF",
        background: "rgba(50, 50, 50, 0.6)",
    },
};

const iconButtonStyles = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 48,
    height: 48,
    padding: 0,
    fontSize: 20,
    color: "#FFFFFF",
    background: "rgba(50, 50, 50, 0.6)",
    borderRadius: "50%",
    border: "2px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.4)",
    cursor: "pointer",
    margin: "0 4px",
    transition: "all 0.15s ease",
};

// ============================================================================
// COMPONENTS
// ============================================================================

/**
 * GlassmorphismButton — Primary/Secondary action button
 */
export const GlassmorphismButton = ({
    label,
    icon,
    variant = "primary",
    loading = false,
    onClick,
    className = "",
    style = {},
}) => {
    const vStyles = variantStyles[variant] || variantStyles.primary;

    const handleMouseEnter = (e) => {
        e.currentTarget.style.color =
            variant === "primary" ? "#D32F2F" : "#333";
        e.currentTarget.style.background = "rgba(255, 255, 255, 0.95)";
        e.currentTarget.style.borderColor = "#FFF";
        e.currentTarget.style.textShadow = "none";
    };

    const handleMouseLeave = (e) => {
        e.currentTarget.style.color = vStyles.color;
        e.currentTarget.style.background = vStyles.background;
        e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.25)";
        e.currentTarget.style.textShadow = "0 1px 2px rgba(0,0,0,0.8)";
    };

    return (
        <button
            type="button"
            className={className}
            disabled={loading}
            onClick={!loading ? onClick : undefined}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{
                ...baseButtonStyles,
                ...vStyles,
                ...style,
                opacity: loading ? 0.7 : 1,
            }}
        >
            {loading ? (
                <span
                    style={{
                        display: "inline-block",
                        width: 20,
                        height: 20,
                        border: "2px solid transparent",
                        borderTopColor: variant === "primary" ? "#FFF" : "#FFF",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                    }}
                />
            ) : (
                <>
                    {icon && <span style={{ fontSize: 22 }}>{icon}</span>}
                    <span>{label}</span>
                </>
            )}
        </button>
    );
};

/**
 * GlassmorphismIconButton — Circular icon button
 */
export const GlassmorphismIconButton = ({
    icon,
    onClick,
    className = "",
    style = {},
    title,
}) => {
    const handleMouseEnter = (e) => {
        e.currentTarget.style.color = "#333";
        e.currentTarget.style.background = "rgba(255, 255, 255, 0.95)";
        e.currentTarget.style.borderColor = "#FFF";
    };

    const handleMouseLeave = (e) => {
        e.currentTarget.style.color = "#FFFFFF";
        e.currentTarget.style.background = "rgba(50, 50, 50, 0.6)";
        e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
    };

    return (
        <button
            type="button"
            className={className}
            onClick={onClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            title={title}
            style={{ ...iconButtonStyles, ...style }}
        >
            {icon}
        </button>
    );
};

export default GlassmorphismButton;
